import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  ShiftManagementMainPageDocument,
  ShiftManagementPublishScheduleDocument,
} from 'generated/resources/graphql'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  formatCustomFilters,
  getCustomFilters,
  isStatusWithoutConflict,
} from 'modules/shift_management/components/ShiftManagement/Content/filters/customFilters'
import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import useLocationIdsFilter from 'modules/shift_management/hooks/useLocationIdsFIlter'
import { useNotifications } from 'modules/shift_management/hooks/useNotifications'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'

const usePublishShifts = () => {
  const [showPublishModal, togglePublishModal] = React.useState<boolean>(false)
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ sendNotifications }] = useNotifications()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()
  const [{ withoutShiftIds, teamIds, employeeIds, customFilters }] = useFilters()
  const [{ canManageShifts }] = usePermissions()
  const locationIds = useLocationIdsFilter()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const newMultiFiltersEnabled = hasFeatureFlag('dev_shift_management_new_multi_filters')

  const [publishSchedule, { loading }] = useMutation(ShiftManagementPublishScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.publishShiftmanagementSchedule,
  })

  const { getFilteredEmployees } = useApplyFilters(shiftsRows)
  const apolloClient = useApolloClient()

  const onConfirm = async () => {
    const employeeIdsOld = getFilteredEmployees().map((e) => e.id)

    const { filters: multiFilters, logicOperator } = getMultiFilters(
      LOCAL_STORAGE_KEYS.shiftEmployees
    )
    const customFiltersOld = getCustomFilters(multiFilters)
    const filterByWithoutConflict = isStatusWithoutConflict(multiFilters)

    const res = await publishSchedule({
      variables: {
        customFilters: newMultiFiltersEnabled
          ? customFilters
          : formatCustomFilters(customFiltersOld),
        employeeIds: newMultiFiltersEnabled ? employeeIds?.map(Number) : employeeIdsOld,
        endAt: view.endAt,
        // Only send location ids if the new entry flow is enabled to avoid double filters
        locationIds: newEntryFlowEnabled ? locationIds : null,
        sendNotifications: sendNotifications,
        startAt: view.startAt,
        teamIds: teamIds,
        withoutShiftIds: newMultiFiltersEnabled || filterByWithoutConflict ? withoutShiftIds : null,
        logicOperator: newMultiFiltersEnabled
          ? LOGIC_OPERATOR.AND
          : logicOperator.get('logic_operator').value,
        viewType: view.viewType,
      },
    })

    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.publish_success'),
      })
      let automaticShiftsRowsCount = 0
      shiftsRows.forEach((row) => {
        row.shifts?.shifts?.forEach((shift) => {
          if (shift.creationMode === 'automatic') {
            automaticShiftsRowsCount++
          }
        })
      })

      const numberOfFilters = newMultiFiltersEnabled
        ? customFilters.length +
          (teamIds?.length ?? 0) +
          (employeeIds?.length ?? 0) +
          locationIds.length
        : multiFilters.length

      await apolloClient.refetchQueries({
        include: [ShiftManagementMainPageDocument],
      })

      track(trackingEvents.shiftManagement.publishShiftManagementSubmit, {
        view: view.viewType,
        view_type_by: view.groupType,
        from_date: view.startAt,
        to_date: view.endAt,
        number_of_filters: numberOfFilters,
        send_notification: sendNotifications,
        automatic_shifts_count: automaticShiftsRowsCount,
        shift_ids: shiftsRows.flatMap((row) => row.shifts?.shifts?.map((shift) => shift.id) || []),
      })

      dispatchUI({ type: 'LOADING' })
      dispatchSchedule({ type: 'PUBLISH_WEEK' })
      dispatchSchedule({ type: 'RESET_VIEW', view })
      dispatchPagination({ type: 'RESET_PAGINATION' })
      dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })

      togglePublishModal(false)
    }
  }

  return {
    submitting: loading,
    showPublishModal: showPublishModal && canManageShifts,
    togglePublishModal,
    onConfirm,
    showPublishButton: canManageShifts,
  }
}

export default usePublishShifts
